import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { musicalWorkLinkService } from "../services/musicalWorkLinkService";
import {
  MusicalWorkLinkValuesInit,
  getMusicalWorkLinkValues,
  MusicalWorkLinkValuesType,
  MusicalWorkLinkErrorsType,
  musicalWorkLinkDisplayId,
} from "./MusicalWorkLinkCommonStore";
import { MusicalWorkLink } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getPlatformValues,
  PlatformValuesType,
} from "../../platform/store/PlatformCommonStore";
import {
  getPlatformLinkTypeValues,
  PlatformLinkTypeValuesType,
} from "../../platformLinkType/store/PlatformLinkTypeCommonStore";
import { confirm } from "../../../common/services/confirmService";
import { getApiAsync } from "../../../api";
import { platformService } from "../../platform/services/platformService";
import { platformLinkTypeService } from "../../platformLinkType/services/platformLinkTypeService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class MusicalWorkLinkCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: MusicalWorkLinkErrorsType = {};
  service = musicalWorkLinkService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: MusicalWorkLink | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = MusicalWorkLinkValuesInit();
  urlError: string | undefined;
  platformError: string | undefined;
  platformService = platformService;
  platformSearchValue: string = "";
  platformOptions: PlatformValuesType[] = [];
  typeError: string | undefined;
  typeService = platformLinkTypeService;
  typeSearchValue: string = "";
  typeOptions: PlatformLinkTypeValuesType[] = [];

  private customValidators: CustomValidator<MusicalWorkLinkValuesType>[] = [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<MusicalWorkLinkValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<MusicalWorkLinkValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return musicalWorkLinkDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.MusicalWorkLinkSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity

      if (params.callerData?.fieldName === "platform") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showPlatform = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.platformOne({
            id: params.returnData.resultId,
          });
          const platform = result.platformOne;
          this.entityValues.showPlatform = {
            displayId: platform.displayId,
            id: platform.id,
          };
          this.setPlatformOptions([
            getPlatformValues(platform, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "type") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showType = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.platformLinkTypeOne({
            id: params.returnData.resultId,
          });
          const platformLinkType = result.platformLinkTypeOne;
          this.entityValues.showType = {
            displayId: platformLinkType.displayId,
            id: platformLinkType.id,
          };
          this.setTypeOptions([
            getPlatformLinkTypeValues(platformLinkType, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupMusicalWorkLinkLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  getUrlValue(): string {
    return this.entityValues.url;
  }

  setUrlValue(value: string) {
    this.entityValues.url = value;
  }

  setPlatformOptions(values: PlatformValuesType[]) {
    this.platformOptions = values;
  }

  *loadPlatformValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Platform");
    const allOptions = cache.getOptions("Platform") as PlatformValuesType[];

    const searchValue = this.platformSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setPlatformOptions(filtered);
  }

  getPlatformOptions() {
    return this.platformOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPlatformValue(value: string) {
    this.platformSearchValue = value;
    yield this.loadPlatformValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedPlatform(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewPlatform(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "platform"
      );
    } else {
      if (value) {
        this.entityValues.showPlatform = value;
        this.entityValues.__platform = value.displayId;
      }
    }
  }

  setTypeOptions(values: PlatformLinkTypeValuesType[]) {
    this.typeOptions = values;
  }

  *loadTypeValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("PlatformLinkType");
    const allOptions = cache.getOptions(
      "PlatformLinkType"
    ) as PlatformLinkTypeValuesType[];

    const searchValue = this.typeSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setTypeOptions(filtered);
  }

  getTypeOptions() {
    return this.typeOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setTypeValue(value: string) {
    this.typeSearchValue = value;
    yield this.loadTypeValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedType(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewPlatformLinkType(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "type"
      );
    } else {
      if (value) {
        this.entityValues.showType = value;
        this.entityValues.__type = value.displayId;
      }
    }
  }

  *addNewPlatform(notFound: string | undefined, callerFieldName?: string): any {
    // QuickCreate checks for fields with quickCreate enabled
    if (notFound && callerFieldName === "platform") {
      const confirmed = yield confirm(
        this.langStore.quickCreateConfirmTitle(this.langStore.PlatformSingular),
        this.langStore.quickCreateConfirmDescription(
          notFound,
          this.langStore.PlatformSingular
        )
      );
      if (!confirmed) return;

      try {
        const api = yield getApiAsync(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.platformCreate({ name: notFound });

        if (result.platformCreate) {
          this.entityValues.showPlatform = {
            id: result.platformCreate.id,
            displayId: result.platformCreate.displayId || notFound,
          };
          this.entityValues.__platform =
            result.platformCreate.displayId || notFound;
        }
      } catch (error) {
        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      }
      return;
    }

    // Fallback to navigation for other fields
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWorkLink",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/platform/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewPlatformLinkType(
    notFound: string | undefined,
    callerFieldName?: string
  ) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWorkLink",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/platformLinkType/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = MusicalWorkLinkValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
    this.platformSearchValue = "";
    this.platformOptions = [];
    this.typeSearchValue = "";
    this.typeOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: MusicalWorkLinkValuesType): MusicalWorkLinkErrorsType {
    const errors: MusicalWorkLinkErrorsType = {};
    this.clearErrors();
    values.url = values.url.trim();
    if (values.url === "") {
      errors.url = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.MusicalWorkLinkUrlLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "MusicalWorkLinkCreateStore"
    );
  }

  *duplicatesMusicalWorkLink(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const musicalWorkLinkInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (musicalWorkLinkInstance) {
      this.entityValues = getMusicalWorkLinkValues(
        musicalWorkLinkInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la vista del registro creado.
      this.rootStore.router.replace(
        `/musicalWorkLink/view/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewMusicalWorkLink(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewMusicalWorkLink(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      url: this.entityValues.url,
      platform: this.entityValues.showPlatform?.id || null,
      type: this.entityValues.showType?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getMusicalWorkLinkValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = MusicalWorkLinkValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    // Subentidad sin alta standalone: se persiste por el create/update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesMusicalWorkLink(duplicateFromId);
      }

      yield this.loadPlatformValues();
      yield this.loadTypeValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
